import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Download, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

export default function Product() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [showPDF, setShowPDF] = useState<boolean>(false);

  const products = t("productsList", { returnObjects: true }) as Array<{
    id: string;
    name: string;
    description: string;
    image: string;
    pdf: string;
  }>;

  const product = products.find((p) => p.id === id);

  const handleDownload = () => {
    if (!product) return;
    const link = document.createElement("a");
    link.href = product.pdf;
    link.download = `${product.name.replace(/\s+/g, "_")}.pdf`;
    link.click();
  };

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Product not found
          </h1>
          <Link
            to="/"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const viewerUrl = `/web/viewer.html?file=${encodeURIComponent(product.pdf)}`;

  return (
    <section className="min-h-screen pt-24 pb-12 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mb-8"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          {t("nav.home")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12"
        >
          <div>
            <img
              src={product.image}
              alt={product.name}
              className="w-full rounded-2xl shadow-2xl"
            />
          </div>

          <div>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {product.name}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
              {product.description}
            </p>

            <div className="flex flex-wrap gap-4 justify-center sm:justify-start">
              <button
                onClick={() => setShowPDF(!showPDF)}
                className="inline-flex items-center justify-center px-6 py-3 sm:px-8 sm:py-4 text-base sm:text-lg font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
              >
                {showPDF ? "Hide PDF" : "View PDF"}
              </button>
              <button
                onClick={handleDownload}
                className="inline-flex items-center justify-center px-6 py-3 sm:px-6 sm:py-4 text-base sm:text-lg font-semibold text-white bg-gray-700 hover:bg-gray-800 rounded-full transition-colors"
              >
                <Download className="w-5 h-5 mr-2" />
                {t("catalog.download")}
              </button>
            </div>
          </div>
        </motion.div>

        {showPDF && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-200 dark:border-gray-700"
          >
            <iframe
              src={viewerUrl}
              className="w-full h-[500px] md:h-[700px]"
              title="PDF Viewer"
            />
          </motion.div>
        )}
      </div>
    </section>
  );
}
